import { GroupOrderUniversal } from '@duxton-web/icons';
import {
  Alert,
  Button,
  Collapse,
  Divider,
  Flex,
  Modal,
  Spin,
  Table,
  Tag,
  Typography,
} from '@duxton-web/ui';
import React from 'react';

import type {
  JourneyCampaignSyncPreviewCountry,
  SyncPreviewDatalakeGroup,
  SyncPreviewCriteria,
  SyncPreviewSheetVariant,
} from 'omega-rtc/types/ts/journey-sheet-sync';

import './JourneyCampaignSyncPreview.scss';

export type { JourneyCampaignSyncPreviewCountry } from 'omega-rtc/types/ts/journey-sheet-sync';

export type SyncPreviewSummary = {
  missingFromDatalake: string[];
  missingFromSheet: SyncPreviewDatalakeGroup[];
};

export const summarizeSyncPreview = (
  preview: JourneyCampaignSyncPreviewCountry,
): SyncPreviewSummary => {
  const sheetGroupIDs = new Set(preview.sheetGroups.map((group) => group.groupID));
  const datalakeGroupIDs = new Set(
    preview.datalakeGroups.map((group) => group.groupID),
  );

  return {
    missingFromDatalake: preview.sheetGroups
      .filter((group) => !datalakeGroupIDs.has(group.groupID))
      .map((group) => group.groupID),
    missingFromSheet: preview.datalakeGroups.filter(
      (group) => !sheetGroupIDs.has(group.groupID),
    ),
  };
};

const getCountryFlag = (countryCode: string) => (/^[a-z]{2}$/i.test(countryCode) ? String.fromCodePoint(
  ...countryCode.toUpperCase().split('').map(
    (character) => 127397 + character.charCodeAt(0),
  ),
) : '');

const formatCriteriaValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.map(formatCriteriaValue).join(', ');
  if (value !== null && typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const renderCriteria = (criteria: SyncPreviewCriteria) => (
  <Flex gap="6px" wrap="wrap">
    {Object.entries(criteria).map(([key, value]) => (
      <Tag key={key}>
        {key}: {formatCriteriaValue(value)}
      </Tag>
    ))}
  </Flex>
);

type GroupComparison = {
  controlGroupPercentage?: number;
  datalakeGroup?: SyncPreviewDatalakeGroup;
  groupID: string;
  isInSheet: boolean;
  variants: SyncPreviewSheetVariant[];
};

const buildGroupComparisons = (
  preview: JourneyCampaignSyncPreviewCountry,
): GroupComparison[] => {
  const datalakeByGroupID = new Map(preview.datalakeGroups.map(
    (group) => [group.groupID, group],
  ));
  const sheetGroupIDs = new Set(preview.sheetGroups.map((group) => group.groupID));
  const sheetComparisons = preview.sheetGroups.map((group) => ({
    controlGroupPercentage: group.controlGroupPercentage,
    datalakeGroup: datalakeByGroupID.get(group.groupID),
    groupID: group.groupID,
    isInSheet: true,
    variants: group.variants,
  }));
  const datalakeOnlyComparisons = preview.datalakeGroups
    .filter((group) => !sheetGroupIDs.has(group.groupID))
    .map((group) => ({
      datalakeGroup: group,
      groupID: group.groupID,
      isInSheet: false,
      variants: [],
    }));

  return [...sheetComparisons, ...datalakeOnlyComparisons];
};

const GroupComparisonDisclosure = ({
  comparison,
}: {
  comparison: GroupComparison;
}) => (
  <Collapse
    bordered={false}
    className="journey-sync-preview-group-collapse"
    defaultActiveKey={[]}
    expandIconPosition="end"
    items={[{
      key: comparison.groupID,
      label: (
        <Flex align="center" gap="10px">
          <GroupOrderUniversal aria-hidden="true" />
          <Flex vertical gap="2px">
            <Typography.Text className="journey-sync-preview-group-label">
              GROUP ID
            </Typography.Text>
            <Typography.Text strong>{comparison.groupID}</Typography.Text>
          </Flex>
        </Flex>
      ),
      extra: (
        <Flex align="center" gap="8px" wrap="wrap">
          {!comparison.isInSheet && <Tag color="warning">Missing from Sheet</Tag>}
          {comparison.controlGroupPercentage !== undefined && (
            <Tag color="gold">
              Control Group {comparison.controlGroupPercentage}%
            </Tag>
          )}
          {comparison.datalakeGroup ? (
            <React.Fragment>
              <Tag color="success">Exists in Datalake</Tag>
              <Tag color="blue">
                {comparison.datalakeGroup.paxCount.toLocaleString()} pax
              </Tag>
            </React.Fragment>
          ) : (
            <Tag color="error">Missing from Datalake</Tag>
          )}
        </Flex>
      ),
      children: (
        <div className="journey-sync-preview-variants">
          <Typography.Text strong>Variants from Google Sheet</Typography.Text>
          <Table
            columns={[
              { dataIndex: 'name', key: 'name', title: 'Variant name' },
              {
                dataIndex: 'criteria',
                key: 'criteria',
                title: 'Criteria',
                render: renderCriteria,
              },
            ]}
            dataSource={comparison.variants.map((variant, index) => ({
              ...variant,
              key: `${comparison.groupID}-${index}`,
            }))}
            locale={{
              emptyText: comparison.isInSheet
                ? 'No variants configured for this group ID.'
                : 'This group ID is missing from Google Sheet.',
            }}
            pagination={{ pageSize: 10 }}
            rowKey="key"
            size="small"
          />
        </div>
      ),
    }]}
  />
);

const SummarySection = ({
  summary,
}: {
  summary: SyncPreviewSummary;
}) => (
  <section className="journey-sync-preview-summary">
    <Typography.Title level={4}>Summary</Typography.Title>
    <div className="journey-sync-preview-summary-grid">
      <div>
        <Typography.Text strong>
          In Sheet, missing from Datalake
        </Typography.Text>
        <div className="journey-sync-preview-summary-list">
          {summary.missingFromDatalake.length ? summary.missingFromDatalake.map(
            (groupID) => <Tag color="error" key={groupID}>{groupID}</Tag>,
          ) : (
            <Typography.Text type="secondary">None</Typography.Text>
          )}
        </div>
      </div>
      <div>
        <Typography.Text strong>
          In Datalake, missing from Sheet
        </Typography.Text>
        <div className="journey-sync-preview-summary-list">
          {summary.missingFromSheet.length ? summary.missingFromSheet.map(
            (group) => (
              <Tag color="warning" key={group.groupID}>
                {group.groupID} · {group.paxCount.toLocaleString()} pax
              </Tag>
            ),
          ) : (
            <Typography.Text type="secondary">None</Typography.Text>
          )}
        </div>
      </div>
    </div>
  </section>
);

const CountryPreview = ({
  defaultExpanded,
  preview,
}: {
  defaultExpanded: boolean;
  preview: JourneyCampaignSyncPreviewCountry;
}) => {
  const summary = summarizeSyncPreview(preview);
  const isReadyToSync = !summary.missingFromDatalake.length;
  const missingGroupCount = summary.missingFromDatalake.length;
  const verdictMessage = isReadyToSync
    ? 'All Google Sheet group IDs exist in Datalake.'
    : `${missingGroupCount} Google Sheet group ID${missingGroupCount === 1 ? '' : 's'} ${missingGroupCount === 1 ? 'is' : 'are'} missing from Datalake.`;

  return (
    <Collapse
      bordered={false}
      className="journey-sync-preview-country-collapse"
      defaultActiveKey={defaultExpanded ? [String(preview.countryID)] : []}
      expandIconPosition="end"
      items={[{
        key: String(preview.countryID),
        label: (
          <div className="journey-sync-preview-country-header">
            <Flex align="center" gap="10px">
              <span className="journey-sync-preview-flag">
                {getCountryFlag(preview.code)}
              </span>
              <Typography.Title level={3} style={{ margin: 0 }}>
                {preview.country}
              </Typography.Title>
            </Flex>
            <Flex
              align="center"
              className="journey-sync-preview-country-verdict"
              gap="10px"
              wrap="wrap"
            >
              <Tag color={isReadyToSync ? 'success' : 'error'}>
                {isReadyToSync ? 'Passed' : 'Not passed'}
              </Tag>
              <Typography.Text type="secondary">
                {verdictMessage}
              </Typography.Text>
            </Flex>
          </div>
        ),
        children: (
          <div className="journey-sync-preview-country-details">
            <div className="journey-sync-preview-groups">
              {buildGroupComparisons(preview).map((comparison) => (
                <GroupComparisonDisclosure
                  comparison={comparison}
                  key={comparison.groupID}
                />
              ))}
            </div>
            <SummarySection summary={summary} />
          </div>
        ),
      }]}
    />
  );
};

export const JourneyCampaignSyncPreviewModal = ({
  applyDisabled = false,
  applying = false,
  applyError = false,
  loading = false,
  previewError = false,
  onRetry,
  countries,
  expandSingleCountry = false,
  onApply,
  onClose,
  open,
}: {
  applyDisabled?: boolean;
  applying?: boolean;
  applyError?: boolean;
  loading?: boolean;
  previewError?: boolean;
  onRetry?: () => void;
  countries: JourneyCampaignSyncPreviewCountry[];
  expandSingleCountry?: boolean;
  onApply: () => void;
  onClose: () => void;
  open: boolean;
}) => (
  <Modal
    className="journey-sync-preview-modal"
    closable={!applying}
    keyboard={!applying}
    maskClosable={!applying}
    footer={(
      <Flex gap="8px" justify="flex-end">
        <Button disabled={applying} onClick={onClose}>Close</Button>
        <Button
          disabled={applyDisabled || loading || previewError || applyError || !countries.length || applying}
          loading={applying}
          onClick={onApply}
          type="primary"
        >
          Apply Sheet Update
        </Button>
      </Flex>
    )}
    onCancel={() => { if (!applying) onClose(); }}
    open={open}
    title="Review Sheet Changes"
    width={1400}
  >
    <div className="journey-sync-preview-scroll-area">
      {loading && (
        <Flex align="center" gap="8px" justify="center" role="status">
          <Spin /> Loading Google Sheet changes…
        </Flex>
      )}
      {previewError && (
        <Alert
          action={onRetry && <Button onClick={onRetry}>Retry</Button>}
          message="Unable to preview the Google Sheet update. Please try again."
          showIcon
          type="error"
        />
      )}
      {applyError && (
        <Alert
          action={onRetry && <Button onClick={onRetry}>Refresh preview</Button>}
          description="The update may have completed. Refresh the preview and verify the campaign state before applying again."
          message="Unable to confirm the Google Sheet update."
          showIcon
          type="error"
        />
      )}
      {!loading && !previewError && !countries.length && (
        <Typography.Paragraph>No campaign previews were returned.</Typography.Paragraph>
      )}
      {!loading && !previewError && countries.map((preview, index) => (
        <React.Fragment key={`${preview.projectID}-${preview.campaignID}-${preview.countryID}`}>
          {index > 0 && <Divider />}
          <CountryPreview
            defaultExpanded={expandSingleCountry && countries.length === 1}
            preview={preview}
          />
        </React.Fragment>
      ))}
    </div>
  </Modal>
);
